/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from 'react';
import Add from '@material-ui/icons/Add';
import { translate } from 'components/Translation';
import { AccountCell, TextCell } from 'components/Cells/Cells';
import { useHistory } from 'react-router';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/manageGroupsStyle';
import Button from 'components/CustomButtons/Button';
import { MoreVert } from '@material-ui/icons';
import Badge from 'components/Badge/Badge';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import GroupDetailsDialog from './subcomponents/GroupDetailsDialog';
import Dropdown from 'components/Dropdown/Dropdown';
import { BodyMBold, BodyS, BodySBold, HeadingMBold } from 'components/Typography/Typography';
import _ from 'lodash';
import CustomPopUpWithLoader from 'components/CustomPopUpWithLoader/CustomPopUpWithLoader';
import ServicesDetailsDialog from './subcomponents/ServicesDetailsDialog';
import { LoyaltyGroup } from 'utils/api/models/prepaidLoyalty/loyaltyGroup';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { zip } from 'rxjs';
import { finalize } from 'rxjs/operators';
import invalidateCache from 'utils/api/invalidateCache';
import { URL_PATHS } from 'views/PrepaidLoyalty/commons/constants';
import CanPerform from 'components/CanPerform/CanPerform';
import { encode } from 'utils/crypto/crypto';
import { config } from 'views/PrepaidLoyalty/config';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import Business from '@material-ui/icons/Business';

// @ts-ignore
const useStyles = makeStyles(styles);

const dialogs = {
	NONE: 'none',
	MANAGE_GROUP_DETAILS: 'manageGroupDetails',
	MANAGE_SERVICES: 'manageServices',
};
interface IManageGroups {
	groups: LoyaltyGroup[];
	refreshGroups: () => void;
	match: {
		path: string;
	};
	accounts: SingleCorpAccount[];
	selectedAccount: ISelectedAccount | null;
	setSelectedAccount: React.Dispatch<React.SetStateAction<ISelectedAccount | null>>;
}

const ManageGroups = ({
	groups,
	refreshGroups,
	match,
	accounts,
	selectedAccount,
	setSelectedAccount,
}: IManageGroups) => {
	const [searchValue, setSearchValue] = useState<string>('');
	const [currentDialog, setCurrentDialog] = useState<string>(dialogs.NONE);
	const [currentGroup, setCurrentGroup] = useState<LoyaltyGroup | undefined>();
	const [selected, setSelected] = useState<number[]>([]);
	const [popUpOpen, setPopUpOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [popUpState, setPopUpState] = useState<boolean>(false);

	const history = useHistory();
	const classes = useStyles();

	// const showNote = groups.some(({ subscriptionsCount }) => subscriptionsCount > 0);

	const addNewGroup = () => {
		history.push(`/dashboard/gift-program/${URL_PATHS.REGISTER_NEW_LOYALTY}`);
	};

	const hideDialog = () => {
		setCurrentDialog(dialogs.NONE);
	};

	const filteredGroups: LoyaltyGroup[] = useMemo(() => {
		return groups.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()));
	}, [groups, searchValue]);

	useEffect(() => {
		setSelected([]);
	}, [searchValue]);

	const selectedGroups: LoyaltyGroup[] = useMemo(() => {
		return _.filter(filteredGroups, (group, index) => selected.indexOf(index) >= 0);
	}, [selected, filteredGroups]);

	const selectedGroupNames: string[] = useMemo(() => {
		return _.map(selectedGroups, (group) => group.name);
	}, [selectedGroups]);

	const popUpHeader: string = useMemo(() => {
		if (popUpState && selectedGroupNames.length > 1) {
			return translate(
				'prepaid.loyalty.delete.groups.pop.up.success.fewSelect.header',
				selectedGroupNames.length
			);
		} else if (popUpState && selectedGroupNames.length === 1) {
			return translate('prepaid.loyalty.delete.groups.pop.up.success.oneSelect.header', selectedGroupNames[0]);
		} else {
			return translate('prepaid.loyalty.delete.groups.pop.up.failure.header');
		}
	}, [selectedGroupNames, popUpState]);

	const ongoingServicesText = useMemo(() => {
		const activeServicesGroups = _.filter(selectedGroups, (group) => group.subscriptionsCount > 0);
		let text = translate('prepaid.loyalty.delete.groups.ongoing.services.stop');
		if (!activeServicesGroups.length) {
			text = translate('questionMark');
		}
		return (
			<BodySBold color="primary-light" disableMargin inline>
				{text}
			</BodySBold>
		);
	}, [selectedGroups]);

	const hasOnGoingServicesSelected = useMemo(() => {
		return !!_.filter(selectedGroups, (group) => group.subscriptionsCount > 0)?.length;
	}, [selectedGroups]);

	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);

	const deleteGroups = () => {
		setPopUpOpen(true);
		setLoading(true);
		zip(
			...selectedGroups.map((selectedGroup) => {
				return LoyaltyGroupService.deleteLoyaltyGroup(selectedGroup.id);
			})
		)
			.pipe(
				finalize(() => {
					setLoading(false);
					invalidateCache(['getLoyaltyGroups'], true);
				})
			)
			.subscribe(
				() => {
					setPopUpState(true);
				},
				() => {
					setPopUpState(false);
				}
			);
	};

	const closeDeleteConfirmationPopup = () => {
		setPopUpOpen(false);
		refreshGroups();
	};

	const addServices = (id: number) => {
		history.push(`${match.path}/${URL_PATHS.ADD_NEW_SERVICES}/${encode(String(id))}`);
	};

	const addTopUp = (id: number) => {
		history.push(`${match.path}/${URL_PATHS.ADD_TOP_UP}/${encode(String(id))}`);
	};

	return (
		<div className={classes.manageGroupsWrapper}>
			<div className={classes.manageGroupsDescriptionWrapper}>
				<HeadingMBold color="secondary-light" disableMargin>
					{translate('path.prepaid.loyalty')}
				</HeadingMBold>
				<ConditionalRender
					show={config.SHOW_TOP_UP}
					onTrue={() => (
						<BodyS color="disabled-light" disableMargin>
							{translate('prepaid.loyalty.manage.groups.description')}
						</BodyS>
					)}
					onFalse={() => (
						<BodyS color="disabled-light" disableMargin>
							{translate('prepaid.loyalty.manage.groups.short.description')}
						</BodyS>
					)}
				/>
			</div>
			<AccountFilterTable
				accounts={accounts}
				setAccount={setSelectedAccount}
				selectedAccount={selectedAccount}
				filterHeader={''}
				filtersValue={{}}
				filters={[]}
				onFilterChange={() => {}}
				columns={[
					{
						Header: translate('prepaid.loyalty.loyalty.name'),
						Cell: ({ original }: { original: LoyaltyGroup }) => <TextCell text={original.name} bold />,
						maxWidth: 200,
					},
					{
						Header: translate('table.organization.name'),
						Cell: ({ original }: { original: LoyaltyGroup }) => {
							const account = accountsMap[original.createdBy];
							return account ? (
								<AccountCell
									text={account.accountName}
									icon={
										<CustomAvatar
											accountId={account.accountId}
											alt="Company Logo"
											placeholderIcon={<Business />}
											// customClasses={classes.inlineIcon}
										/>
									}
								/>
							) : null;
						},
						accessor: 'account.accountName',
					},
					{
						Cell: ({ original }: { original: LoyaltyGroup }) => (
							<div>
								<ConditionalRender
									show={!!original.subscriptionsCount}
									onTrue={() => (
										<Badge color="success">
											<BodyMBold color="secondary-light" disableMargin>
												{translate(
													original.subscriptionsCount > 1
														? 'prepaid.loyalty.active.subscriptions'
														: 'prepaid.loyalty.active.subscription',
													original.subscriptionsCount
												)}
											</BodyMBold>
										</Badge>
									)}
								/>
							</div>
						),
					},
					{
						width: 140,
						Cell: ({ original }: { original: LoyaltyGroup }) => (
							<CanPerform
								action="prepaid-loyalty:add-services"
								yes={
									<div className={classes.buttonWrapper}>
										<Button
											onClick={() => addServices(original.id)}
											color="tertiary-light"
											withIcon
										>
											<>
												<Add />
												{translate('prepaid.loyalty.add.services')}
											</>
										</Button>
									</div>
								}
							/>
						),
					},
					config.SHOW_TOP_UP && {
						width: 120,
						Cell: ({ original }: { original: LoyaltyGroup }) => (
							<CanPerform
								action="prepaid-loyalty:add-top-up"
								yes={
									<div className={classes.buttonWrapper}>
										<Button color="primary-light" withIcon onClick={() => addTopUp(original.id)}>
											<>
												<Add />
												{translate('prepaid.loyalty.add.top.up')}
											</>
										</Button>
									</div>
								}
							/>
						),
					},
					{
						width: 84,
						Cell: ({ original }: { original: LoyaltyGroup }) => (
							<div className={classes.moreWrapper}>
								<Dropdown
									menu={<MoreVert />}
									items={[
										{
											text: translate('prepaid.loyalty.manage.groups.details.select'),
											onClick: () => {
												setCurrentDialog(dialogs.MANAGE_GROUP_DETAILS);
												setCurrentGroup(original);
											},
											divider: true,
										},
										{
											text: translate('prepaid.loyalty.manage.service.details.select'),
											onClick: () => {
												setCurrentDialog(dialogs.MANAGE_SERVICES);
												setCurrentGroup(original);
											},
											hidden: false,
										},
									]}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
								/>
							</div>
						),
					},
				].filter(Boolean)}
				selected={selected}
				onSelect={(value: any) => setSelected(value)}
				selectPanel={{
					selectedElements: selected.length,
					confirmationDialogButtonDeleteHidden: hasOnGoingServicesSelected,
					confirmationDialogBody: (
						<>
							{!hasOnGoingServicesSelected ? (
								<>
									<BodyS color="primary-light" disableMargin inline>
										{translate('prepaid.loyalty.delete.groups.wantDeleteText')}
									</BodyS>
									<div className={classes.descriptionWrapper}>
										{selectedGroups.length > 1 ? (
											<BodySBold color="primary-light" disableMargin inline>
												{selectedGroups.length + translate('prepaid.loyalty.delete.groups')}
											</BodySBold>
										) : (
											<BodySBold color="primary-light" disableMargin inline>
												{!!selectedGroups.length ? selectedGroups[0].name : undefined}
											</BodySBold>
										)}
									</div>
									<div className={classes.descriptionWrapper}>{ongoingServicesText}</div>
								</>
							) : (
								<div className={classes.descriptionWrapper}>
									{translate('prepaid.loyalty.delete.groups.noDeleteText')}
								</div>
							)}

							{/* <CreditCardMethodText selectedGroups={selectedGroups} /> */}
						</>
					),
					deleteItems: deleteGroups,
				}}
				data={filteredGroups}
				searchInputValue={searchValue}
				setSearchInputValue={(value) => setSearchValue(value)}
				buttons={{
					text: (
						<>
							<Add />
							{translate('prepaid.loyalty.add.group')}
						</>
					),
					onClick: addNewGroup,
					action: 'prepaid-loyalty:add-group',
				}}
				loading={false}
			/>

			<GroupDetailsDialog
				group={currentGroup}
				setCurrentGroup={setCurrentGroup}
				hideDialog={hideDialog}
				isOpen={currentDialog === dialogs.MANAGE_GROUP_DETAILS}
				refreshGroups={refreshGroups}
			/>

			<ServicesDetailsDialog
				group={currentGroup}
				setOpen={(isOpen: boolean) => {
					setCurrentGroup(undefined);
					setCurrentDialog(isOpen ? dialogs.MANAGE_SERVICES : dialogs.NONE);
				}}
				isOpen={currentDialog === dialogs.MANAGE_SERVICES}
				refreshGroups={refreshGroups}
			/>

			<CustomPopUpWithLoader
				isOpen={popUpOpen}
				hideDialog={closeDeleteConfirmationPopup}
				loading={loading}
				isError={!popUpState}
				header={[popUpHeader]}
				body={!popUpState ? '' : translate('prepaid.loyalty.delete.groups.pop.up.success.body')}
			/>
		</div>
	);
};

export default ManageGroups;
